import React, { useState, useEffect } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import ReactPaginate from 'react-paginate';
import '../../assets/css/table-mobile-responsiveness.css';
import '../../assets/css/react-confirmation-style.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import supabase from '../../supabaseClient'
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const VPO_DEPARTMENT = 'Vice President of Operations';

const EmployeeDirectory = () => {
  const user = useSelector((state) => state.auth.user);
  const deleted_by = user.id;
  const edited_by = user.id;
  const edited_by_username = user.user_name
  const deleted_by_username = user.user_name

  const [users, setUsers] = useState([]);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [departments, setDepartments] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [selectedUserEdit, setSelectedUserEdit] = useState({
    user_name: '',
    first_name: '',
    last_name: '',
    department: '',
    role: '',
  });

  const roles = ['Superadmin', 'Employee', 'Requestor'];
  const [isModalOpenPassword, setIsModalOpenPassword] = useState(false);

  const openEditModal = (user) => {
    setSelectedUserEdit(user); // includes user_id
    setIsModalOpenEdit(true);
  };
  
  const closeEditModal = () => {
    setIsModalOpenEdit(false);
    setSelectedUserEdit(null);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setIsModalOpenPassword(true);
  };

  const handleEditChange = (field, value) => {
    setSelectedUserEdit((prev) => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleEditSubmit = async (e) => {
  e.preventDefault();

  const {
    user_id,
    user_name,
    first_name,
    last_name,
    department,
    role
  } = selectedUserEdit || {};

  if (!user_id || !user_name || !first_name || !last_name || !department || !role) {
    toast.error("All fields are required!", { position: "top-right" });
    return;
  }

  // Build a clean payload the API expects
  const payload = {
    user_id,
    user_name,
    first_name,
    last_name,
    department,
    role,
    edited_by,           // from Redux
    edited_by_username,  // from Redux
  };

  // Snapshot current values for fallback verification
  const expected = { user_name, first_name, last_name, department, role };

  try {
    const res = await axios.post(
      "https://track-trace-server-production.up.railway.app/api/superadmin/editUser",
      payload,
      { headers: { "Content-Type": "application/json" } }
    );

    // Treat 200/201 as success
    if (res.status === 200 || res.status === 201) {
      toast.success(res.data?.message || "User updated successfully!", { position: "top-right" });
      closeEditModal();
      setSelectedUserEdit(null);
      return;
    }

    // Non-200 but no throw: show message
    toast.error(res.data?.message || "Failed to update user. Please try again.", { position: "top-right" });
  } catch (error) {
    // If backend 500 but DB updated, we’ll confirm and treat as success to avoid confusing UX
    const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
    const status = error?.response?.status;

    // Fallback verification: read row from Supabase and compare
    try {
      const { data: verifyRows, error: verifyErr } = await supabase
        .from("users")
        .select("user_id, user_name, first_name, last_name, department, role")
        .eq("user_id", user_id)
        .limit(1);

      if (!verifyErr && Array.isArray(verifyRows) && verifyRows.length === 1) {
        const v = verifyRows[0];
        const actuallyUpdated =
          v.user_name === expected.user_name &&
          v.first_name === expected.first_name &&
          v.last_name === expected.last_name &&
          v.department === expected.department &&
          v.role === expected.role;

        if (actuallyUpdated) {
          // It updated but the API threw after the update
          toast.success("User updated successfully!", { position: "top-right" });
          closeEditModal();
          setSelectedUserEdit(null);
          return;
        }
      }
    } catch (_) {
      // ignore verification errors — we’ll show the server error toast below
    }

    // If we reach here, it didn’t update; show a useful error
    toast.error(
      serverMsg || (status ? `Update failed with status ${status}` : "Error updating user. Please try again."),
      { position: "top-right" }
    );
    console.error("Error updating user:", error);
  }
};


  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/departments?status=active`);
        const depts = response.data.departments || [];
        const deptNames = depts.map(d => d.name);
        setDepartments(
          deptNames.includes(VPO_DEPARTMENT) ? deptNames : [...deptNames, VPO_DEPARTMENT]
        );
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    fetchDepartments();
  }, [API_URL]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('user_id, user_name,first_name, last_name, department, role');
      if (error) {
        console.error('Error fetching data:', error.message);
      } else {
        setUsers(data);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const userChannel = supabase
      .channel('public:users')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'users' },
        (payload) => {
          setUsers((prevUsers) =>
            prevUsers.map((u) => (u.user_id === payload.new.user_id ? payload.new : u))
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'users' },
        (payload) => {
          setUsers((prevUsers) => prevUsers.filter((u) => u.user_id !== payload.old.user_id));
        }
      )
      .subscribe();

    return () => {
      userChannel.unsubscribe();
    };
  }, []);

  const handleDeleteUser = async (user_id, deleted_by, deleted_by_username) => {
    try {
      const response = await axios.post('https://track-trace-server-production.up.railway.app/api/superadmin/delete-employee', {
        user_id, 
        deleted_by,
        deleted_by_username
      });
      toast.success(response.data.message, {position: 'top-right'});
    } catch (error) {
      console.error('Error deleting user:', error.response ? error.response.data : error.message);
      toast.error("Error deleting user", {position: 'top-right'});
    }
  }

  // === FROM YOUR WORKING FILE (unchanged) ===
  const handleUserNameChange = (e) => {
    const { name, value } = e.target;
    const updatedCredentials = { ...selectedUserEdit, [name]: value };

    if (name === "department" || name === "first_name" || name === "last_name") {
      const { department, first_name, last_name } = updatedCredentials;

      if (department) {
        const departmentPrefixes = {
          brokerage: { default: "brokerage", short: "br" },
          accounting: { default: "accounting", short: "ac" },
          finance: { default: "finance", short: "fc" },
          hr: { default: "hr", short: "hr" },
          freight: { default: "freight", short: "fr" },
          manager: { default: "manager", short: "gm" },
          "vice president of operations": { default: "vpo", short: "vpo" },
        };

        const departmentInfo = departmentPrefixes[department.toLowerCase()] || { default: "brokerage", short: "br" };
        const departmentPrefix = !first_name && !last_name ? departmentInfo.default : departmentInfo.short;

        const formattedName =
          first_name && last_name
            ? `${first_name.trim().charAt(0).toUpperCase() + first_name.trim().slice(1).toLowerCase()}${last_name.trim().charAt(0).toUpperCase() + last_name.trim().slice(1).toLowerCase()}`
            : "";

        let newUserName = `${departmentPrefix}01-${formattedName}`;
        updatedCredentials.user_name = newUserName;
      }
    }

    setSelectedUserEdit(updatedCredentials);
  };
  // ==========================================

  function handleDelete(row) {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/20 relative max-w-md mx-auto">
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 bg-red/10 hover:bg-red/20 rounded-xl flex items-center justify-center text-red hover:text-red transition-all duration-200">
            <i className="fas fa-times text-lg"></i>
          </button>
          <div className="text-center">
            <div className="w-16 h-16 bg-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red text-2xl"></i>
            </div>
            <h2 className="text-xl font-bold text-blue mb-2">Confirm Delete</h2>
            <p className="text-grey mb-6">Are you sure you want to delete employee <span className="font-semibold text-blue">{row.user_name}</span>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-2xl transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteUser(row.user_id, deleted_by, deleted_by_username)
                  onClose();
                }}
                className="flex-1 px-4 py-3 bg-red hover:bg-red/80 text-white font-medium rounded-2xl transition-all duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )
    });
  }

  const closeModal = () => {
    setSelectedUser(null);
  };

  const getDepartmentLabel = (department) => {
    if (department === VPO_DEPARTMENT) {
      return "VPO";
    }
    return department;
  };

  // Search (safe for any user_id type)
  const filteredData = users
    .filter(row =>
      (row.user_id && row.user_id.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.user_name && row.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.department && row.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.role && row.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (getDepartmentLabel(row.department) && String(getDepartmentLabel(row.department)).toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .filter(row => (statusFilter === '' ? true : row.department === statusFilter));

  const handlePageClick = (data) => setCurrentPage(data.selected);

  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handleSubmit = async (e, edited_by) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill out both password fields.", { position: 'top-right' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Password does not match!", { position: 'top-right' });
      return
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long!", { position: 'top-right' });
      return
    }

    try {
      const user_id = selectedUser.user_id;
      const response = await axios.post('https://track-trace-server-production.up.railway.app/api/superadmin/change-password', {
        user_id,  
        newPassword,
        edited_by,
        edited_by_username
      });
      toast.success(response.data.message, { position: 'top-right' });
      closeModal();
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error("Error resetting password", { position: 'top-right' });
    }
  }

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'superadmin':
        return 'bg-red text-white';
      case 'employee':
        return 'bg-blue text-white';
      case 'requestor':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getDepartmentColor = (department) => {
    const colors = {
      'Brokerage': 'bg-blue/10 text-blue border border-blue/30',
      'Finance': 'bg-green-100 text-green-700 border border-green-300',
      'Accounting': 'bg-purple-100 text-purple-700 border border-purple-300',
      'HR': 'bg-pink-100 text-pink-700 border border-pink-300',
      'Freight': 'bg-orange-100 text-orange-700 border border-orange-300',
      'Manager': 'bg-indigo-100 text-indigo-700 border border-indigo-300',
      'General Manager': 'bg-cyan-100 text-cyan-700 border border-cyan-300',
      'Executive Assistant': 'bg-teal-100 text-teal-700 border border-teal-300',
      'President': 'bg-yellow/20 text-blue border border-yellow/50',
      'Executive Vice President': 'bg-purple-500 text-white border border-purple-400'
    };
    return colors[department] || 'bg-gray-100 text-gray-700 border border-gray-300';
  };

  return (
    <section className="flex flex-col justify-center font-poppins bg-gray-100 relative overflow-auto">
      <div className="relative flex flex-col justify-center py-6 px-4">
        {/* Enhanced Header - Matching CreateUser */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            
            <h1 className="text-4xl font-bold text-blue">
              Employee Directory
            </h1>
          </div>
          <p className="text-grey font-medium">
            Manage your team members and their information
          </p>
        </div>

        {/* Modern Form Container - Matching CreateUser */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden mb-8">
            {/* Form Header */}
            <div className="bg-blue px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <i className="fas fa-search text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">Search & Filter</h2>
                  <p className="text-white/80 text-sm">Find and filter employees easily</p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Search Section */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                   
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-search text-blue text-xs"></i>
                      Search Term
                    </label>
                    <input
                      type="text"
                      placeholder="Search by ID, Username, Department, Role..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                    />
                  </div>
                </div>

                {/* Department Filter */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-filter text-blue text-xs"></i>
                      Filter by Department
                    </label>
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value="">All Departments</option>
                        <option value="Brokerage">Brokerage</option>
                        <option value="Accounting">Accounting</option>
                        <option value="Finance">Finance</option>
                        <option value="HR">HR</option>
                        <option value="Freight">Freight</option>
                        <option value="Manager">Manager</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                   
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-eye text-blue text-xs"></i>
                      Rows per Page
                    </label>
                    <div className="relative">
                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(0);
                        }}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                      >
                        <option value={10}>10 rows</option>
                        <option value={20}>20 rows</option>
                        <option value={50}>50 rows</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <i className="fas fa-chevron-down text-blue text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Table/Loading Section */}
        {loading ? (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              <div className="p-12">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                    <div className="w-16 h-16 border-4 border-transparent border-t-yellow rounded-full animate-spin absolute inset-0 animate-ping"></div>
                  </div>
                  <div className="text-blue font-medium text-lg">Loading employees...</div>
                  <div className="text-grey text-sm">Please wait while we fetch your data</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-7xl mx-auto">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              {/* Table Header */}
              <div className="bg-blue px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <i className="fas fa-table text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">Employee Data</h3>
                    <p className="text-white/80 text-sm">
                      {paginatedData.length} of {filteredData.length} employees
                    </p>
                  </div>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table className="w-full">
                  <Thead>
                    <Tr className="bg-gray-50 border-b border-gray-200">
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          Employee ID
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          Username
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          First Name
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          Last Name
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          Department
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-left font-semibold text-sm text-blue">
                        <div className="flex items-center gap-2">
                          Role
                        </div>
                      </Th>
                      <Th className="px-6 py-4 text-center font-semibold text-sm text-blue bg-gray-200 sticky right-0">
                        <div className="flex items-center justify-center gap-2">
                          Actions
                        </div>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <Tr key={index} className="hover:bg-blue/5 transition-all duration-200 border-b border-gray-100 group">
                          <Td className="px-6 py-4 bg-blue/5 font-mono text-sm font-semibold text-blue">
                            {row.user_id}
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue/20 rounded-full flex items-center justify-center">
                                <i className="fas fa-user text-blue text-xs"></i>
                              </div>
                              {row.user_name}
                            </div>
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey">
                            {row.first_name}
                          </Td>
                          <Td className="px-6 py-4 font-medium text-grey">
                            {row.last_name}
                          </Td>
                          <Td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(row.department)}`}>
                              <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                              {getDepartmentLabel(row.department)}
                            </span>
                          </Td>
                          <Td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getRoleColor(row.role)}`}>
                              <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                              {row.role}
                            </span>
                          </Td>
                          <Td className="px-6 py-4 bg-gray-200 sticky right-0">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => openEditModal(row)}
                                className="group bg-blue hover:bg-blue/80 text-white px-3 py-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
                              >
                                <i className="fas fa-edit text-xs group-hover:animate-pulse"></i>
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(row)}
                                className="group bg-red hover:bg-red/80 text-white px-3 py-2 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
                              >
                                <i className="fas fa-trash text-xs group-hover:animate-pulse"></i>
                                Delete
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} className="text-center py-12">
                          <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-blue/10 rounded-full flex items-center justify-center">
                              <i className="fas fa-users text-blue text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-blue mb-2">No Employees Found</h3>
                              <p className="text-grey">Try adjusting your search or filter criteria</p>
                            </div>
                          </div>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300"
                    >
                      {/* Header Row */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <i className="fas fa-user text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-blue text-lg truncate">
                            {row.user_name}
                          </h3>
                          <p className="text-sm text-grey">ID: {row.user_id}</p>
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(row.department)}`}>
                          <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                          {getDepartmentLabel(row.department)}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getRoleColor(row.role)}`}>
                          <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                          {row.role}
                        </span>
                      </div>
                      
                      {/* Info Grid */}
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-signature text-blue text-sm"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-grey truncate">
                              {row.first_name} {row.last_name}
                            </div>
                            <div className="text-xs text-grey/70">Full Name</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(row)}
                          className="flex-1 bg-blue hover:bg-blue/80 text-white py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                          <i className="fas fa-edit"></i>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(row)}
                          className="flex-1 bg-red hover:bg-red/80 text-white py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                          <i className="fas fa-trash"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-users text-blue text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-blue mb-2">No Employees Found</h3>
                    <p className="text-grey">Try adjusting your search or filter criteria</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Pagination */}
        {Math.ceil(filteredData.length / rowsPerPage) > 1 && (
          <div className="w-full max-w-7xl mx-auto mt-6">
            <div className="rounded-3xl shadow-xl border border-white/20 overflow-hidden">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
                <div className="text-sm text-grey font-medium text-center sm:text-left">
                  Showing <span className="font-semibold text-blue">{currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, filteredData.length)}</span> of <span className="font-semibold text-blue">{filteredData.length}</span> employees
                </div>
                
                <div className="w-full sm:w-auto">
                  <ReactPaginate
                    previousLabel={
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chevron-left text-xs"></i>
                        <span className="hidden xs:inline text-sm">Previous</span>
                      </div>
                    }
                    nextLabel={
                      <div className="flex items-center gap-2">
                        <span className="hidden xs:inline text-sm">Next</span>
                        <i className="fas fa-chevron-right text-xs"></i>
                      </div>
                    }
                    breakLabel={"..."}
                    pageCount={Math.ceil(filteredData.length / rowsPerPage)}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={2}
                    onPageChange={handlePageClick}
                    containerClassName="flex items-center justify-center gap-2"
                    pageClassName="flex-shrink-0"
                    pageLinkClassName="w-10 h-10 rounded-xl border border-gray-200 hover:border-blue/30 flex items-center justify-center text-sm font-medium text-grey hover:text-blue hover:bg-blue/5 transition-all duration-200"
                    activeClassName="!bg-blue !border-blue !rounded-xl shadow-md"
                    activeLinkClassName="!text-white font-semibold hover:!text-white"
                    previousClassName="flex-shrink-0 px-4 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                    previousLinkClassName="flex items-center gap-2 text-sm font-medium text-grey hover:text-blue"
                    nextClassName="flex-shrink-0 px-4 py-2 rounded-xl border border-gray-200 hover:border-blue/30 hover:bg-blue/5 transition-all duration-200"
                    nextLinkClassName="flex items-center gap-2 text-sm font-medium text-grey hover:text-blue"
                    disabledClassName="opacity-50 cursor-not-allowed hover:bg-transparent hover:border-gray-200 hover:text-grey"
                    breakClassName="px-2 py-2 text-grey text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Password Modal */}
        {isModalOpenPassword && selectedUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl w-full max-w-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
              {/* Modal Header */}
              <div className="bg-blue px-8 py-6 relative">
                <button 
                  onClick={closeModal} 
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white hover:text-white transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <i className="fas fa-key text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-2xl">Change Password</h2>
                    <p className="text-white/80">Create new password for {selectedUser.user_name}</p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={(e) => handleSubmit(e, edited_by)} className="p-8">
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-lock text-blue text-xs"></i>
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md pr-12"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-grey hover:text-blue transition-colors duration-200 p-1"
                      >
                        {passwordVisible ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-lock text-blue text-xs"></i>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md pr-12"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-grey hover:text-blue transition-colors duration-200 p-1"
                      >
                        {passwordVisible ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                      </button>
                    </div>
                    <p className="text-xs text-grey/70 mt-2 flex items-center gap-1">
                      <i className="fas fa-info-circle text-blue text-xs"></i>
                      Must contain 8+ characters with uppercase, lowercase & numbers
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
                  <button 
                    type="button"
                    onClick={closeModal} 
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-2xl transition-all duration-200 hover:shadow-md"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="px-6 py-3 bg-blue hover:bg-blue/80 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <i className="fas fa-key mr-2"></i>
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modern Edit Modal */}
        {isModalOpenEdit && selectedUserEdit && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl w-full max-w-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
              {/* Modal Header */}
              <div className="bg-blue px-8 py-6 relative">
                <button 
                  onClick={closeEditModal} 
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white hover:text-white transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <i className="fas fa-user-edit text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-2xl">Edit Employee</h2>
                    <p className="text-white/80">Update information for {selectedUserEdit.first_name} {selectedUserEdit.last_name}</p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleEditSubmit} className="p-8">
                <div className="space-y-6">
                  {/* Username Field - Full Width */}
                  <div>
                    <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                      <i className="fas fa-user text-blue text-xs"></i>
                      Username
                    </label>
                    <input
                      type="text"
                      name="user_name"
                      value={selectedUserEdit.user_name}
                      onChange={handleUserNameChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                    />
                  </div>

                  {/* 2x2 Grid for other fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={selectedUserEdit.first_name}
                        onChange={handleUserNameChange}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={selectedUserEdit.last_name}
                        onChange={handleUserNameChange}
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-building text-blue text-xs"></i>
                        Department
                      </label>
                      <div className="relative">
                        <select
                          name="department"
                          value={selectedUserEdit.department}
                          onChange={handleUserNameChange}
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                        >
                          <option value="" disabled>Select Department</option>
                          {departments.map((dept) => (
                            <option key={dept} value={dept}>
                              {dept === VPO_DEPARTMENT ? "Vice President of Operations (VPO)" : dept}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <i className="fas fa-chevron-down text-blue text-sm"></i>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-grey font-semibold mb-3 text-sm">
                        <i className="fas fa-shield-alt text-blue text-xs"></i>
                        Role
                      </label>
                      <div className="relative">
                        <select
                          name="role"
                          value={selectedUserEdit.role}
                          onChange={(e) => handleEditChange("role", e.target.value)}
                          className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md appearance-none"
                        >
                          <option value="" disabled>Select Role</option>
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <i className="fas fa-chevron-down text-blue text-sm"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
                  <button 
                    type="button"
                    onClick={closeEditModal} 
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-2xl transition-all duration-200 hover:shadow-md"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancel
                  </button>
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        closeEditModal();
                        openPasswordModal(selectedUserEdit);
                      }}
                      className="px-6 py-3 bg-yellow hover:bg-yellow/80 text-blue font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-key mr-2"></i>
                      Change Password
                    </button>
                    
                    <button 
                      type="submit"
                      className="px-6 py-3 bg-blue hover:bg-blue/80 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-save mr-2"></i>
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default EmployeeDirectory;
